import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  Classroom,
  ClassroomStudent,
  ClassroomInvitation,
  ClassAnalytics,
  AssignmentWithStats,
} from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  Users,
  TrendingUp,
  Target,
  BookOpen,
  Plus,
  Send,
  UserMinus,
  Eye,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  ArrowLeftRight,
  RefreshCw,
  Upload,
  FileText,
  Clock,
} from "lucide-react";
import CurriculumTab from "../components/CurriculumTab";
import AssignmentSubmissionsDialog from "../components/AssignmentSubmissionsDialog";
import { BulkStudentImport } from "../components/BulkStudentImport";
import { AssignmentTemplateManager } from "../components/AssignmentTemplateManager";
import { DateRangePicker } from "../components/DateRangePicker";
import { useAuth } from "../context/AuthContext";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchoolTeacher = !!user?.school_id;
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<Classroom | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  // Create classroom modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    grade_level: "",
    subject: "Mathematics",
    max_students: 30,
    school_name: "",
    academic_year: "2024-2025",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  // Invite student modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteIdentifier, setInviteIdentifier] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");

  // Student roster state
  const [students, setStudents] = useState<ClassroomStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Invitations state
  const [invitations, setInvitations] = useState<ClassroomInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState<ClassAnalytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);


  // Assignment state
  const [assignments, setAssignments] = useState<AssignmentWithStats[]>([]);
  const [activeAssignments, setActiveAssignments] = useState<
    AssignmentWithStats[]
  >([]);
  const [cancelledAssignments, setCancelledAssignments] = useState<
    AssignmentWithStats[]
  >([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [viewSubmissionsDialog, setViewSubmissionsDialog] = useState<{
    open: boolean;
    assignmentId: string;
    title: string;
  }>({ open: false, assignmentId: "", title: "" });
  const [createAssignmentModalOpen, setCreateAssignmentModalOpen] =
    useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    title: "",
    description: "",
    assignment_type: "technique_practice",
    technique_ids: "",
    min_problems: 20,
    min_accuracy: 80,
    due_date: "",
    points_possible: 100,
  });

  // Cancel assignment state
  const [cancellingAssignmentId, setCancellingAssignmentId] = useState<
    string | null
  >(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // Bulk import state
  const [bulkImportModalOpen, setBulkImportModalOpen] = useState(false);

  // Template manager state
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);

  // Regenerate class code state
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [oldClassCode, setOldClassCode] = useState("");
  const [newClassCode, setNewClassCode] = useState("");

  // Date range state for analytics
  const [dateRangeDays, setDateRangeDays] = useState(30);

  useEffect(() => {
    loadClassrooms();
  }, []);

  const loadClassrooms = async () => {
    try {
      setLoading(true);
      const data = await api.getClassrooms();
      setClassrooms(data);
      if (data.length > 0 && !selectedClassroom) {
        selectClassroom(data[0]);
      }
    } catch (error) {
      console.error("Failed to load classrooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectClassroom = async (classroom: Classroom) => {
    setSelectedClassroom(classroom);
    loadStudents(classroom.id);
    loadInvitations(classroom.id);
    loadAnalytics(classroom.id);
    loadAssignments(classroom.id);
  };

  const loadStudents = async (classroomId: string) => {
    try {
      setLoadingStudents(true);
      const data = await api.getClassroomStudents(classroomId);
      setStudents(data);
    } catch (error) {
      console.error("Failed to load students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadInvitations = async (classroomId: string) => {
    try {
      setLoadingInvitations(true);
      const data = await api.getClassroomInvitations(classroomId);
      setInvitations(data);
    } catch (error) {
      console.error("Failed to load invitations:", error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const loadAnalytics = async (classroomId: string, days?: number) => {
    try {
      setLoadingAnalytics(true);
      const data = await api.getClassAnalytics(
        classroomId,
        days || dateRangeDays,
      );
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleDateRangeChange = (days: number) => {
    setDateRangeDays(days);
    if (selectedClassroom) {
      loadAnalytics(selectedClassroom.id, days);
    }
  };

  const loadAssignments = async (classroomId: string) => {
    try {
      setLoadingAssignments(true);

      const [activeData, allData] = await Promise.all([
        api.getTeacherAssignments(classroomId, false),
        api.getTeacherAssignments(classroomId, true),
      ]);

      setActiveAssignments(activeData.assignments);

      // Load all assignments (including cancelled)
      const cancelled = allData.assignments.filter(
        (a) => a.is_active === false,
      );
      setCancelledAssignments(cancelled);

      // Keep old assignments state for compatibility
      setAssignments(activeData.assignments);
    } catch (error) {
      console.error("Failed to load assignments:", error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const createClassroom = async () => {
    if (!createForm.name.trim()) {
      setCreateError("Classroom name is required");
      return;
    }

    setCreating(true);
    setCreateError("");
    setCreateSuccess("");

    try {
      const result = await api.createClassroom(createForm);
      setCreateSuccess(
        `Classroom created! Class code: ${result.classroom.class_code}`,
      );
      setCreateForm({
        name: "",
        description: "",
        grade_level: "",
        subject: "Mathematics",
        max_students: 30,
        school_name: "",
        academic_year: "2024-2025",
      });
      await loadClassrooms();
      setTimeout(() => {
        setCreateModalOpen(false);
        setCreateSuccess("");
      }, 2000);
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : "Failed to create classroom",
      );
    } finally {
      setCreating(false);
    }
  };

  const inviteStudent = async () => {
    if (!selectedClassroom || !inviteIdentifier.trim()) {
      setInviteError("Please enter a student username or email");
      return;
    }

    setInviting(true);
    setInviteError("");
    setInviteSuccess("");

    try {
      const isEmail = inviteIdentifier.includes("@");
      const result = await api.inviteStudent({
        classroom_id: selectedClassroom.id,
        [isEmail ? "student_email" : "student_username"]:
          inviteIdentifier.trim(),
      });
      setInviteSuccess(result.message);
      setInviteIdentifier("");
      // Reload invitations to show the new one
      await loadInvitations(selectedClassroom.id);
      setTimeout(() => {
        setInviteModalOpen(false);
        setInviteSuccess("");
      }, 2000);
    } catch (error) {
      setInviteError(
        error instanceof Error ? error.message : "Failed to send invitation",
      );
    } finally {
      setInviting(false);
    }
  };

  const removeStudent = async (studentId: string, studentName: string) => {
    if (!selectedClassroom) return;

    if (!confirm(`Remove ${studentName} from ${selectedClassroom.name}?`)) {
      return;
    }

    try {
      await api.removeStudent(selectedClassroom.id, studentId);
      await loadStudents(selectedClassroom.id);
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to remove student",
      );
    }
  };

  const viewStudentPerformance = (student: ClassroomStudent) => {
    navigate(`/student-analytics/${student.student_id}`);
  };

  const createAssignment = async () => {
    if (!selectedClassroom || !assignmentForm.title.trim()) {
      return;
    }

    try {
      const content: Record<string, any> = {};

      if (assignmentForm.assignment_type === "technique_practice") {
        content.technique_ids = assignmentForm.technique_ids
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
        content.min_problems = assignmentForm.min_problems;
        content.min_accuracy = assignmentForm.min_accuracy;
      }

      await api.createAssignment({
        classroom_id: selectedClassroom.id,
        title: assignmentForm.title,
        description: assignmentForm.description,
        assignment_type: assignmentForm.assignment_type,
        content,
        grading_criteria: {
          accuracy_threshold: assignmentForm.min_accuracy,
          min_problems: assignmentForm.min_problems,
        },
        points_possible: assignmentForm.points_possible,
        due_date: assignmentForm.due_date || undefined,
        auto_grade: true,
        is_published: true,
      });

      setCreateAssignmentModalOpen(false);
      setAssignmentForm({
        title: "",
        description: "",
        assignment_type: "technique_practice",
        technique_ids: "",
        min_problems: 20,
        min_accuracy: 80,
        due_date: "",
        points_possible: 100,
      });
      await loadAssignments(selectedClassroom.id);
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to create assignment",
      );
    }
  };

  const handleCancelAssignment = (assignmentId: string) => {
    setCancellingAssignmentId(assignmentId);
    setCancelConfirmOpen(true);
  };

  const confirmCancelAssignment = async () => {
    if (!selectedClassroom || !cancellingAssignmentId) return;

    try {
      await api.cancelAssignment(cancellingAssignmentId);
      setCancelConfirmOpen(false);
      setCancellingAssignmentId(null);
      await loadAssignments(selectedClassroom.id);
    } catch (error) {
      alert(
        error instanceof Error ? error.message : "Failed to cancel assignment",
      );
    }
  };

  const copyClassCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert("Class code copied to clipboard!");
  };

  const handleRegenerateCode = async () => {
    if (!selectedClassroom) return;

    try {
      setRegenerating(true);
      setOldClassCode(selectedClassroom.class_code);

      const result = await api.regenerateClassCode(selectedClassroom.id);
      setNewClassCode(result.classroom.class_code);

      // Update local state
      setSelectedClassroom(result.classroom);
      setClassrooms(
        classrooms.map((c) =>
          c.id === result.classroom.id ? result.classroom : c,
        ),
      );
    } catch (error: any) {
      console.error("Error regenerating class code:", error);
      alert(`Failed to regenerate code: ${error.message}`);
      setRegenerateModalOpen(false);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <GraduationCap className="w-8 h-8" />
          Teacher Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your classrooms and track student progress
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Classroom List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  My Classrooms
                </span>
                {!isSchoolTeacher && (
                  <Button size="sm" onClick={() => setCreateModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {classrooms.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-400" />
                  <p className="text-sm text-gray-500 mb-4">
                    {isSchoolTeacher
                      ? "Your school admin will create classrooms for you"
                      : "No classrooms yet"}
                  </p>
                  {!isSchoolTeacher && (
                    <Button onClick={() => setCreateModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Classroom
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {classrooms.map((classroom) => (
                    <Button
                      key={classroom.id}
                      variant={
                        selectedClassroom?.id === classroom.id
                          ? "default"
                          : "outline"
                      }
                      className="w-full justify-start h-auto py-3"
                      onClick={() => selectClassroom(classroom)}
                    >
                      <div className="w-full text-left">
                        <div className="font-semibold">{classroom.name}</div>
                        <div className="text-xs opacity-80">
                          {classroom.grade_level &&
                            `${classroom.grade_level} • `}
                          {classroom.subject}
                        </div>
                        <div className="text-xs opacity-70 font-mono mt-1">
                          Code: {classroom.class_code}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Selected Classroom Details */}
        <div className="lg:col-span-2">
          {selectedClassroom ? (
            <Tabs defaultValue="overview" className="space-y-4">
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="students">Students</TabsTrigger>
                  <TabsTrigger value="analytics">Analytics</TabsTrigger>
                  <TabsTrigger value="assignments">Assignments</TabsTrigger>
                  <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
                </TabsList>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyClassCode(selectedClassroom.class_code)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Class Code
                  </Button>
                  {!isSchoolTeacher && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRegenerateModalOpen(true)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate Code
                    </Button>
                  )}
                </div>
              </div>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{selectedClassroom.name}</CardTitle>
                    <CardDescription>
                      {selectedClassroom.description || "No description"}
                    </CardDescription>
                  </CardHeader>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Total Students
                      </CardTitle>
                      <Users className="h-4 w-4 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {analytics?.analytics?.total_students || 0}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Enrolled</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Active Students
                      </CardTitle>
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {analytics?.analytics?.active_students || 0}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Class Accuracy
                      </CardTitle>
                      <Target className="h-4 w-4 text-purple-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {analytics?.analytics?.class_metrics?.avg_accuracy?.toFixed(
                          1,
                        ) || 0}
                        %
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Average</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Assignments
                      </CardTitle>
                      <BookOpen className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {assignments.length}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Created</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    <Button onClick={() => setInviteModalOpen(true)}>
                      <Send className="w-4 h-4 mr-2" />
                      Invite Student
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/teacher/create-assignment?classroom_id=${selectedClassroom.id}`,
                        )
                      }
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Assignment
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Students Tab */}
              <TabsContent value="students" className="space-y-4">
                {/* Pending Invitations */}
                {invitations.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Pending Invitations</CardTitle>
                      <CardDescription>
                        Students you've invited who haven't joined yet
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingInvitations ? (
                        <div className="text-center py-4">
                          Loading invitations...
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {invitations.map((invitation) => (
                            <div
                              key={invitation.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div>
                                <p className="font-medium">
                                  {invitation.student_email ||
                                    invitation.student_username ||
                                    "Unknown"}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Sent:{" "}
                                  {new Date(
                                    invitation.sent_at,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge
                                variant={
                                  invitation.status === "pending"
                                    ? "default"
                                    : invitation.status === "accepted"
                                      ? "default"
                                      : invitation.status === "declined"
                                        ? "destructive"
                                        : "secondary"
                                }
                              >
                                {invitation.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Student Roster</CardTitle>
                      <div className="flex items-center gap-2">
                        {students.length >= 2 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              navigate(
                                `/teacher/compare?classroom=${selectedClassroom.id}`,
                              )
                            }
                          >
                            <ArrowLeftRight className="w-4 h-4 mr-2" />
                            Compare Students
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setBulkImportModalOpen(true)}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Bulk Invite
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setInviteModalOpen(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Invite Student
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingStudents ? (
                      <div className="text-center py-4">
                        Loading students...
                      </div>
                    ) : students.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-400" />
                        <p className="text-sm text-gray-500 mb-4">
                          No students enrolled yet
                        </p>
                        <Button onClick={() => setInviteModalOpen(true)}>
                          <Send className="w-4 h-4 mr-2" />
                          Invite Your First Student
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {students.map((student) => (
                          <div
                            key={student.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {student.student_info?.username || "Unknown"}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {student.student_info?.email || "No email"}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge
                                    variant={
                                      student.status === "active"
                                        ? "default"
                                        : "secondary"
                                    }
                                  >
                                    {student.status}
                                  </Badge>
                                  <span className="text-xs text-gray-500">
                                    Enrolled:{" "}
                                    {new Date(
                                      student.enrolled_at,
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewStudentPerformance(student)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {student.status === "active" && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() =>
                                    removeStudent(
                                      student.student_id,
                                      student.student_info?.username ||
                                        "this student",
                                    )
                                  }
                                >
                                  <UserMinus className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Class Analytics</CardTitle>
                    <CardDescription>
                      <DateRangePicker
                        value={dateRangeDays}
                        onChange={handleDateRangeChange}
                        className="mt-4"
                      />
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingAnalytics ? (
                      <div className="text-center py-4">
                        Loading analytics...
                      </div>
                    ) : analytics ? (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-4 border rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">
                              {analytics?.analytics?.class_metrics?.avg_problems_solved?.toFixed(
                                0,
                              ) || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              Avg Problems Solved
                            </div>
                          </div>
                          <div className="text-center p-4 border rounded-lg">
                            <div className="text-2xl font-bold text-green-600">
                              {analytics?.analytics?.class_metrics?.avg_accuracy?.toFixed(
                                1,
                              ) || 0}
                              %
                            </div>
                            <div className="text-sm text-gray-600">
                              Avg Accuracy
                            </div>
                          </div>
                          <div className="text-center p-4 border rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">
                              {analytics?.analytics?.class_metrics
                                ?.total_techniques_mastered || 0}
                            </div>
                            <div className="text-sm text-gray-600">
                              Techniques Mastered
                            </div>
                          </div>
                        </div>

                        {analytics?.analytics?.top_performers &&
                          analytics.analytics.top_performers.length > 0 && (
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-green-600" />
                                Top Performers
                              </h4>
                              <div className="space-y-2">
                                {analytics?.analytics?.top_performers
                                  ?.slice(0, 5)
                                  .map((performer) => (
                                    <div
                                      key={performer.user_id}
                                      className="flex items-center justify-between p-3 border rounded"
                                    >
                                      <span className="font-medium">
                                        {performer.student_info?.username ||
                                          "Unknown"}
                                      </span>
                                      <div className="flex items-center gap-4 text-sm">
                                        <span>
                                          {performer.accuracy?.toFixed(1) || 0}%
                                          accuracy
                                        </span>
                                        <span>
                                          {performer.problems_solved || 0}{" "}
                                          problems
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                        {analytics?.analytics?.struggling_students &&
                          analytics.analytics.struggling_students.length >
                            0 && (
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-orange-600" />
                                Students Needing Support
                              </h4>
                              <div className="space-y-2">
                                {analytics?.analytics?.struggling_students
                                  ?.slice(0, 5)
                                  .map((student) => (
                                    <div
                                      key={student.user_id}
                                      className="flex items-center justify-between p-3 border rounded bg-orange-50 dark:bg-orange-900/10"
                                    >
                                      <span className="font-medium">
                                        {student.student_info?.username ||
                                          "Unknown"}
                                      </span>
                                      <div className="flex items-center gap-4 text-sm">
                                        <span>
                                          {student.accuracy?.toFixed(1) || 0}%
                                          accuracy
                                        </span>
                                        <span>
                                          {student.days_inactive || 0} days
                                          inactive
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No analytics data available
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Assignments Tab */}
              <TabsContent value="assignments" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Assignments</CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setTemplateManagerOpen(true)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Manage Templates
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/teacher/create-assignment?classroom_id=${selectedClassroom.id}`,
                            )
                          }
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Assignment
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="active" className="w-full">
                      <TabsList>
                        <TabsTrigger value="active">
                          Active ({activeAssignments.length})
                        </TabsTrigger>
                        <TabsTrigger value="scheduled">
                          Scheduled (
                          {
                            assignments.filter(
                              (a) =>
                                !a.is_published &&
                                a.publish_date &&
                                a.is_active,
                            ).length
                          }
                          )
                        </TabsTrigger>
                        <TabsTrigger value="cancelled">
                          Cancelled ({cancelledAssignments.length})
                        </TabsTrigger>
                      </TabsList>

                      {/* Active Assignments Tab */}
                      <TabsContent value="active">
                        {loadingAssignments ? (
                          <div className="text-center py-4">
                            Loading assignments...
                          </div>
                        ) : activeAssignments.length === 0 ? (
                          <div className="text-center py-8">
                            <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-400" />
                            <p className="text-sm text-gray-500 mb-4">
                              No active assignments
                            </p>
                            <Button
                              onClick={() =>
                                navigate(
                                  `/teacher/create-assignment?classroom_id=${selectedClassroom.id}`,
                                )
                              }
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Create Your First Assignment
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {activeAssignments.map((assignment) => (
                              <div
                                key={assignment.id}
                                className="p-4 border rounded-lg"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-semibold">
                                      {assignment.title}
                                    </h4>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {assignment.description}
                                    </p>
                                    <div className="flex items-center gap-3 mt-2 text-sm">
                                      <Badge>
                                        {assignment.assignment_type.replace(
                                          /_/g,
                                          " ",
                                        )}
                                      </Badge>
                                      {assignment.due_date && (
                                        <span className="text-gray-500">
                                          Due:{" "}
                                          {new Date(
                                            assignment.due_date,
                                          ).toLocaleDateString()}
                                        </span>
                                      )}
                                      <span className="text-gray-500">
                                        {assignment.points_possible} points
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-right text-sm">
                                      <div className="font-semibold">
                                        {assignment.stats.completion_rate.toFixed(
                                          0,
                                        )}
                                        %
                                      </div>
                                      <div className="text-gray-500">
                                        {assignment.stats.submitted}/
                                        {assignment.stats.total_students}{" "}
                                        submitted
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          navigate(
                                            `/teacher/assignment-submissions?assignment_id=${assignment.id}&title=${encodeURIComponent(assignment.title)}`,
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          navigate(
                                            `/teacher/edit-assignment/${assignment.id}`,
                                          )
                                        }
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() =>
                                          handleCancelAssignment(assignment.id)
                                        }
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      {/* Scheduled Assignments Tab */}
                      <TabsContent value="scheduled">
                        {assignments.filter(
                          (a) => !a.is_published && a.publish_date && a.is_active,
                        ).length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No scheduled assignments
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {assignments
                              .filter(
                                (a) =>
                                  !a.is_published && a.publish_date && a.is_active,
                              )
                              .map((assignment) => {
                                const publishDate = new Date(
                                  assignment.publish_date!,
                                );
                                const now = new Date();
                                const timeUntilPublish =
                                  publishDate.getTime() - now.getTime();
                                const hoursUntilPublish = Math.floor(
                                  timeUntilPublish / (1000 * 60 * 60),
                                );
                                const minutesUntilPublish = Math.floor(
                                  (timeUntilPublish % (1000 * 60 * 60)) /
                                    (1000 * 60),
                                );

                                return (
                                  <div
                                    key={assignment.id}
                                    className="p-4 border rounded-lg bg-blue-50"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <h4 className="font-semibold">
                                            {assignment.title}
                                          </h4>
                                          <Badge variant="secondary">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Scheduled
                                          </Badge>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">
                                          {assignment.description}
                                        </p>
                                        <div className="flex items-center gap-3 mt-2 text-sm">
                                          <span className="text-blue-600 font-medium">
                                            Publishes in{" "}
                                            {hoursUntilPublish > 0 &&
                                              `${hoursUntilPublish}h `}
                                            {minutesUntilPublish}m
                                          </span>
                                          <span>
                                            on{" "}
                                            {publishDate.toLocaleDateString()}{" "}
                                            at{" "}
                                            {publishDate.toLocaleTimeString()}
                                          </span>
                                        </div>
                                        {assignment.due_date && (
                                          <div className="text-sm text-gray-500 mt-1">
                                            Due:{" "}
                                            {new Date(
                                              assignment.due_date,
                                            ).toLocaleDateString()}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            navigate(
                                              `/teacher/edit-assignment/${assignment.id}`,
                                            )
                                          }
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() =>
                                            handleCancelAssignment(assignment.id)
                                          }
                                        >
                                          <XCircle className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </TabsContent>

                      {/* Cancelled Assignments Tab */}
                      <TabsContent value="cancelled">
                        {cancelledAssignments.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No cancelled assignments
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {cancelledAssignments.map((assignment) => (
                              <div
                                key={assignment.id}
                                className="p-4 border rounded-lg bg-gray-50 opacity-75"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold">
                                        {assignment.title}
                                      </h4>
                                      <Badge variant="destructive">
                                        Cancelled
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {assignment.description}
                                    </p>
                                    <div className="flex items-center gap-3 mt-2 text-sm">
                                      <Badge>
                                        {assignment.assignment_type.replace(
                                          /_/g,
                                          " ",
                                        )}
                                      </Badge>
                                      {assignment.due_date && (
                                        <span className="text-gray-500">
                                          Due:{" "}
                                          {new Date(
                                            assignment.due_date,
                                          ).toLocaleDateString()}
                                        </span>
                                      )}
                                      <span className="text-gray-500">
                                        {assignment.points_possible} points
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-right text-sm">
                                      <div className="font-semibold">
                                        {assignment.stats.completion_rate.toFixed(
                                          0,
                                        )}
                                        %
                                      </div>
                                      <div className="text-gray-500">
                                        {assignment.stats.submitted}/
                                        {assignment.stats.total_students}{" "}
                                        submitted
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          navigate(
                                            `/teacher/assignment-submissions?assignment_id=${assignment.id}&title=${encodeURIComponent(assignment.title)}`,
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Curriculum Tab */}
              <TabsContent value="curriculum" className="space-y-4">
                <CurriculumTab
                  classroomId={selectedClassroom.id}
                  classroomName={selectedClassroom.name}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a classroom to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Classroom Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New Classroom</DialogTitle>
            <DialogDescription>
              Create a new classroom and get a unique class code for students to
              join.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Classroom Name *</Label>
              <Input
                id="name"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
                placeholder="e.g., Grade 4 Math"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm({ ...createForm, description: e.target.value })
                }
                placeholder="Brief description of the class"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="grade_level">Grade Level</Label>
                <Input
                  id="grade_level"
                  value={createForm.grade_level}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      grade_level: e.target.value,
                    })
                  }
                  placeholder="e.g., Grade 4"
                />
              </div>
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={createForm.subject}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, subject: e.target.value })
                  }
                  placeholder="Mathematics"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_students">Max Students</Label>
                <Input
                  id="max_students"
                  type="number"
                  value={createForm.max_students}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      max_students: parseInt(e.target.value) || 30,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="academic_year">Academic Year</Label>
                <Input
                  id="academic_year"
                  value={createForm.academic_year}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      academic_year: e.target.value,
                    })
                  }
                  placeholder="2024-2025"
                />
              </div>
            </div>
            {createError && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}
            {createSuccess && (
              <Alert className="bg-green-50 text-green-900 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>{createSuccess}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createClassroom} disabled={creating}>
              {creating ? "Creating..." : "Create Classroom"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Student Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Student</DialogTitle>
            <DialogDescription>
              Enter the student's username or email address to send an
              invitation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="identifier">Student Username or Email</Label>
              <Input
                id="identifier"
                value={inviteIdentifier}
                onChange={(e) => setInviteIdentifier(e.target.value)}
                placeholder="username or email@example.com"
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  !inviting &&
                  inviteIdentifier &&
                  inviteStudent()
                }
              />
            </div>
            {inviteError && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{inviteError}</AlertDescription>
              </Alert>
            )}
            {inviteSuccess && (
              <Alert className="bg-green-50 text-green-900 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>{inviteSuccess}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={inviteStudent}
              disabled={inviting || !inviteIdentifier}
            >
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create Assignment Modal */}
      <Dialog
        open={createAssignmentModalOpen}
        onOpenChange={setCreateAssignmentModalOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Assignment</DialogTitle>
            <DialogDescription>
              Create a new assignment for your students
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="assignment-title">Title *</Label>
              <Input
                id="assignment-title"
                value={assignmentForm.title}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    title: e.target.value,
                  })
                }
                placeholder="e.g., Multiplication Practice"
              />
            </div>
            <div>
              <Label htmlFor="assignment-description">Description</Label>
              <Textarea
                id="assignment-description"
                value={assignmentForm.description}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    description: e.target.value,
                  })
                }
                placeholder="Instructions for students"
              />
            </div>
            <div>
              <Label htmlFor="assignment-type">Assignment Type</Label>
              <select
                id="assignment-type"
                className="w-full p-2 border rounded"
                value={assignmentForm.assignment_type}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    assignment_type: e.target.value,
                  })
                }
              >
                <option value="technique_practice">Technique Practice</option>
                <option value="problem_set">Problem Set</option>
                <option value="timed_challenge">Timed Challenge</option>
                <option value="accuracy_challenge">Accuracy Challenge</option>
              </select>
            </div>
            {assignmentForm.assignment_type === "technique_practice" && (
              <>
                <div>
                  <Label htmlFor="technique-ids">
                    Technique IDs (comma-separated)
                  </Label>
                  <Input
                    id="technique-ids"
                    value={assignmentForm.technique_ids}
                    onChange={(e) =>
                      setAssignmentForm({
                        ...assignmentForm,
                        technique_ids: e.target.value,
                      })
                    }
                    placeholder="e.g., abc123, def456"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min-problems">Min Problems</Label>
                    <Input
                      id="min-problems"
                      type="number"
                      value={assignmentForm.min_problems}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          min_problems: parseInt(e.target.value) || 20,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="min-accuracy">Min Accuracy (%)</Label>
                    <Input
                      id="min-accuracy"
                      type="number"
                      value={assignmentForm.min_accuracy}
                      onChange={(e) =>
                        setAssignmentForm({
                          ...assignmentForm,
                          min_accuracy: parseInt(e.target.value) || 80,
                        })
                      }
                    />
                  </div>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="due-date">Due Date</Label>
                <Input
                  id="due-date"
                  type="datetime-local"
                  value={assignmentForm.due_date}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      due_date: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="points">Points Possible</Label>
                <Input
                  id="points"
                  type="number"
                  value={assignmentForm.points_possible}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      points_possible: parseInt(e.target.value) || 100,
                    })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateAssignmentModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={createAssignment}
              disabled={!assignmentForm.title.trim()}
            >
              Create Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Assignment Confirmation Dialog */}
      <Dialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Assignment?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The assignment will be cancelled and
              moved to the Cancelled tab.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Students will no longer see this assignment or be able to submit
              work. All existing submissions will be preserved for your records.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelConfirmOpen(false)}
            >
              Keep Assignment
            </Button>
            <Button variant="destructive" onClick={confirmCancelAssignment}>
              Cancel Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Submissions Dialog */}
      <AssignmentSubmissionsDialog
        assignmentId={viewSubmissionsDialog.assignmentId}
        assignmentTitle={viewSubmissionsDialog.title}
        open={viewSubmissionsDialog.open}
        onOpenChange={(open) =>
          setViewSubmissionsDialog({ ...viewSubmissionsDialog, open })
        }
      />

      {/* Bulk Import Dialog */}
      {selectedClassroom && (
        <BulkStudentImport
          open={bulkImportModalOpen}
          onOpenChange={setBulkImportModalOpen}
          classroomId={selectedClassroom.id}
          onComplete={() => {
            if (selectedClassroom) {
              loadInvitations(selectedClassroom.id);
              loadStudents(selectedClassroom.id);
            }
          }}
        />
      )}

      {/* Template Manager Dialog */}
      <AssignmentTemplateManager
        open={templateManagerOpen}
        onOpenChange={setTemplateManagerOpen}
      />

      {/* Regenerate Class Code Dialog */}
      <Dialog open={regenerateModalOpen} onOpenChange={setRegenerateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Class Code</DialogTitle>
            <DialogDescription>
              Generate a new class code for this classroom. The old code will no
              longer work.
            </DialogDescription>
          </DialogHeader>
          {!newClassCode ? (
            <div className="py-4">
              <Alert className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Students using the old code will no longer be able to join.
                  Make sure to share the new code with your students.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-gray-600">
                Current code:{" "}
                <span className="font-mono font-bold">
                  {selectedClassroom?.class_code}
                </span>
              </p>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 mb-2">
                  Class code regenerated successfully!
                </p>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-green-700">Old code:</p>
                    <p className="font-mono font-bold text-gray-400 line-through">
                      {oldClassCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-green-700">New code:</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold text-lg text-green-800">
                        {newClassCode}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyClassCode(newClassCode)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {!newClassCode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setRegenerateModalOpen(false)}
                  disabled={regenerating}
                >
                  Cancel
                </Button>
                <Button onClick={handleRegenerateCode} disabled={regenerating}>
                  {regenerating ? "Regenerating..." : "Regenerate Code"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setRegenerateModalOpen(false);
                  setNewClassCode("");
                  setOldClassCode("");
                }}
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
